const STORAGE_KEY = 'acm_ad_click';
const GCLID_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const FBCLID_TTL_MS = 7 * 24 * 60 * 60 * 1000;

type StoredClick = {
  gclid?: { id: string; ts: number };
  fbclid?: { id: string; ts: number };
};

export type AdSource = 'google' | 'meta';

function hasMarketingConsent(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(window.CC && window.CC.allowedCategory && window.CC.allowedCategory('marketing'));
}

function readStorage(): StoredClick {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredClick) : {};
  } catch {
    return {};
  }
}

function writeStorage(data: StoredClick): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore (private mode, quota, etc.)
  }
}

export function captureAdClickIds(search: string): void {
  if (!search || !hasMarketingConsent()) return;
  const params = new URLSearchParams(search);
  const gclid = params.get('gclid');
  const fbclid = params.get('fbclid');
  if (!gclid && !fbclid) return;

  const stored = readStorage();
  const now = Date.now();
  if (gclid) stored.gclid = { id: gclid, ts: now };
  if (fbclid) stored.fbclid = { id: fbclid, ts: now };
  writeStorage(stored);
}

export function clearAdClickIds(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function getActiveAdSource(): AdSource | null {
  if (!hasMarketingConsent()) return null;
  const stored = readStorage();
  const now = Date.now();
  const gclidValid = stored.gclid && now - stored.gclid.ts < GCLID_TTL_MS;
  const fbclidValid = stored.fbclid && now - stored.fbclid.ts < FBCLID_TTL_MS;

  if (gclidValid && fbclidValid) {
    return stored.gclid!.ts >= stored.fbclid!.ts ? 'google' : 'meta';
  }
  if (gclidValid) return 'google';
  if (fbclidValid) return 'meta';
  return null;
}
